import { useEffect } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { GroupTypes } from "../../../abstract/controller/enum/GroupTypes";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import {
  getStatusPositioned,
  setDrag,
  setIdle,
  setPosition,
} from "../../../abstract/redux/slice/statusSlice";
import { handleParameterMove } from "../../../abstract/redux/slice/structureSlice";
import { DragOverlay } from "../../ui/overlay/DragOverlay";
import { showParameterDrop } from "../parameter/logic/showParameterDrop";
import styles from "./CanvasElementPanel.module.scss";

function CanvasElementPanel({ node }) {
  const _dispatch = useDispatch();
  const _isStatusPositioned = useSelector(getStatusPositioned);

  const _id = nodeUtils.getID(node);

  const [{ _nodeDrag, _isOver, _canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.PARAMETER,
      collect: (monitor) => ({
        _nodeDrag: monitor.getItem()?.element,
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        return showParameterDrop(item.element, node, null, true);
      },
      drop(item) {
        _dispatch(setIdle());

        // Canvas parameters are always at the same level.
        _dispatch(handleParameterMove(nodeUtils.getID(item.element), _id));
      },
    }),
    [_id, node]
  );

  useEffect(() => {
    if (!_canDrop) {
      return;
    }

    if (_isOver) {
      const group = pathUtils.determineGroup(nodeUtils.getID(_nodeDrag), _id);

      if (group === GroupTypes.INTERNAL_FUNCTIONS || group === GroupTypes.INTERNAL_GLOBALS) {
        _dispatch(setPosition({ id: nodeUtils.getID(_nodeDrag), hidden: true }));
      } else {
        _dispatch(setPosition({ id: nodeUtils.getID(_nodeDrag), external: true }));
      }
    } else {
      _dispatch(setDrag({ fromPosition: true }));
    }
  }, [_id, _nodeDrag, _isOver, _canDrop, _dispatch]);

  const _isNodeOver = _isStatusPositioned && _isOver;

  return (
    <div ref={drop}>{_isNodeOver ? <PanelDrag isNodeOver={_isNodeOver} /> : <PanelDefault />}</div>
  );
}

const PanelDrag = ({ isNodeOver }) => {
  return (
    <div key="canvas-element-panel-drag" className={styles["panel--drag"]}>
      <DragOverlay type={ItemTypes.PARAMETER} isVisible={isNodeOver} />
    </div>
  );
};

const PanelDefault = () => {
  return <div key="canvas-element-panel" className={styles["panel"]} />;
};

export default CanvasElementPanel;
